import { Box, Drawer, Hidden, List, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import NavItem from './NavItem';

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 280
  },
  desktopDrawer: {
    width: 256,
    top: 46,
    height: 'calc(100% - 46px)',
    marginTop: '20px'
  },
  avatar: {
    cursor: 'pointer',
    width: 46,
    height: 46
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: 'Helvetica',
    fontSize: '14px',
    color: '#45485E',
    marginBottom: '10px'
  },
  nested: {
    paddingLeft: 25
  }
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();

  const dataLogin = useSelector(state => state.authSlice.dataLogin);
  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isUser = useSelector(state => state.authSlice.isUser);
  const isStaff = useSelector(state => state.authSlice.isStaff);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const isAccountant = useSelector(state => state.authSlice.isAccountant);
  const isSale = useSelector(state => state.authSlice.isSale);
  const isCustomerCare = useSelector(state => state.authSlice.isCustomerCare);
  const isLeadMarketing = useSelector(state => state.authSlice.isLeadMarketing);
  const isTechnician = useSelector(state => state.authSlice.isTechnician);
  const isDriver = useSelector(state => state.authSlice.isDriver);
  const agencyLevel = Number(useSelector(state => state.authSlice.agencyLevel));

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const menuStatistics = [
    {
      href: `/app/manage/statistics/warehouse-transfer`,
      title: 'Thống kê kích hoạt'
    },
    // {
    //   href: `/app/manage/statistics/active`,
    //   title: 'Thống kê kích hoạt'
    // },
    {
      href: `/app/manage/statistics/service`,
      title: 'Thống kê dịch vụ'
    }
  ];

  const menuDevices = isAdmin
    ? [
      {
        href: `/app/manage/service-packages`,
        title: 'Quản lý gói dịch vụ'
      },
      {
        href: `/app/manage/vehicle-types`,
        title: 'Quản lý loại phương tiện'
      },
      {
        href: `/app/manage/devices`,
        title: 'Quản lý thiết bị'
      },
      {
        href: `/app/manage/versions`,
        title: 'Quản lý phiên bản thiết bị'
      },
      {
        href: `/app/manage/products`,
        title: 'Quản lý phụ kiện'
      }
    ]
    : [
      {
        href: `/app/manage/devices`,
        title: 'Quản lý thiết bị'
      },
      ...(isTechnician
        ? [{
            href: `/app/manage/versions`,
            title: 'Quản lý phiên bản thiết bị'
          }]
        : []),
      ...((isAgency || isAccountant || isSale || isLeadMarketing)
        ? [{
            href: `/app/manage/products`,
            title: 'Quản lý phụ kiện'
          }]
        : [])
    ];

  const menuAdvertisement = [
    {
      href: `/app/manage/broadcasts-schedules`,
      title: 'Quản lý lịch phát'
    },
    {
      href: `/app/manage/files-audio`,
      title: 'Quản lý file-audio'
    },
    {
      href: `/app/manage/files-video`,
      title: 'Quản lý file-video'
    }
  ];

  const menuDrivers = [
    {
      href: `/app/manage/driver`,
      title: 'Danh sách lái xe'
    },
    ...(isUser
      ? [
        {
          href: `/app/manage/accounts-driver`,
          title: 'Tài khoản lái xe'
        }
      ]
      : [])
  ];

  const customerCareContent = (
    <Box height="100%" display="flex" flexDirection="column">
      <Box style={{ paddingTop: '16px' }}>
        <List>
          <Box className={classes.menuItem}>
            <NavItem
              title="Tech Hub"
              href="/app/manage/device-lookup"
              icon={'/static/images/administration/quan_tri_thiet_bi.svg'}
            />
          </Box>
          <Box className={classes.menuItem}>
            <NavItem
              title="Quản trị khách hàng"
              href="/app/manage/users"
              icon={'/static/images/administration/quan_tri_nguoi_dung.svg'}
            />
          </Box>
          <Box className={classes.menuItem}>
            <NavItem
              title="Quản trị đại lý"
              href="/app/manage/agencies"
              icon={'/static/images/administration/quan_tri_dai_ly.svg'}
            />
          </Box>
          <Box className={classes.menuItem}>
            <NavItem
              title="Quản trị thiết bị"
              href="/app/manage/devices"
              icon={'/static/images/administration/quan_tri_thiet_bi.svg'}
            />
          </Box>
          <Box className={classes.menuItem}>
            <NavItem
              title="Quản trị kinh doanh"
              href="/app/manage/sales-orders"
              icon={'/static/images/administration/quan_tri_nguoi_dung.svg'}
            />
          </Box>
        </List>
      </Box>
      <Box flexGrow={1} />
    </Box>
  );

  const rolesReady = Boolean(dataLogin);

  const emptyContent = (
    <Box height="100%" display="flex" flexDirection="column">
      <Box flexGrow={1} />
    </Box>
  );

  // Mỗi entry mô tả 1 mục trong sidebar chính (không áp dụng cho customerCareContent,
  // vốn là danh sách cố định riêng). `visible` quyết định mục có hiển thị hay không;
  // `wrapInMenuItemBox` giữ đúng UI cũ — một số mục được bọc thêm Box.menuItem, số khác thì không.
  const mainMenuItems = [
    {
      key: 'statistics',
      visible: !isUser && !isStaff && !isAccountant && !isSale && !isLeadMarketing && !isTechnician && !isDriver,
      wrapInMenuItemBox: false,
      icon: '/static/images/bcdn/baocao_dn_tonghop_theonhom.svg',
      title: 'Thống kê',
      arrayNested: menuStatistics
    },
    {
      key: 'tech-hub',
      visible: isAdmin || isTechnician || isSale || isLeadMarketing || isCustomerCare,
      wrapInMenuItemBox: true,
      icon: '/static/images/administration/quan_tri_thiet_bi.svg',
      title: 'Tech Hub',
      href: '/app/manage/device-lookup'
    },
    {
      key: 'customers-admin',
      visible: isAdmin,
      wrapInMenuItemBox: true,
      icon: '/static/images/administration/quan_tri_nguoi_dung.svg',
      title: 'Quản trị khách hàng',
      href: '/app/manage/users'
    },
    {
      key: 'customers-agency',
      visible: isAgency || isSale || isLeadMarketing,
      wrapInMenuItemBox: true,
      icon: '/static/images/administration/quan_tri_nguoi_dung.svg',
      title: 'Quản trị khách hàng',
      href: '/app/manage/customers'
    },
    {
      key: 'agencies',
      visible: isAdmin || isAgency || isAccountant || isSale || isLeadMarketing,
      wrapInMenuItemBox: true,
      icon: '/static/images/administration/quan_tri_dai_ly.svg',
      title: 'Quản trị đại lý',
      href: '/app/manage/agencies'
    },
    {
      key: 'staff-or-sale',
      visible: isLeadMarketing || (!isUser && !isStaff && !isAccountant && !isSale && !isTechnician && !isDriver),
      wrapInMenuItemBox: true,
      icon: '/static/images/administration/quan_tri_nhan_vien.svg',
      title: isLeadMarketing ? 'Quản lý sale' : 'Quản trị nhân viên',
      href: '/app/manage/service-staffs'
    },
    {
      key: 'devices',
      visible: !isUser && !isDriver,
      wrapInMenuItemBox: false,
      icon: '/static/images/administration/quan_tri_thiet_bi.svg',
      title: 'Quản trị thiết bị',
      arrayNested: menuDevices
    },
    {
      key: 'drivers',
      visible: !isAdmin && !isStaff && !isAccountant && !isSale && !isLeadMarketing && !isTechnician && !isDriver,
      wrapInMenuItemBox: false,
      icon: '/static/images/nav-baocaolaixequa4h.svg',
      title: 'Quản trị lái xe',
      arrayNested: menuDrivers
    },
    {
      key: 'advertisement',
      visible: isAdmin,
      wrapInMenuItemBox: false,
      icon: '/static/images/administration/quan_tri_quang_cao.svg',
      title: 'Quản trị quảng cáo',
      arrayNested: menuAdvertisement
    },
    {
      key: 'feedbacks',
      visible: isAdmin,
      wrapInMenuItemBox: false,
      icon: '/static/images/administration/chat.svg',
      title: 'Phản hồi khách hàng',
      href: '/app/manage/feedbacks'
    },
    {
      key: 'sales-orders',
      visible: isAdmin || isAccountant || isSale || isLeadMarketing || isCustomerCare || isAgency,
      wrapInMenuItemBox: true,
      icon: '/static/images/administration/quan_tri_nguoi_dung.svg',
      title: isAgency ? 'Quản trị bán hàng' : 'Quản trị kinh doanh',
      href: '/app/manage/sales-orders'
    },
    {
      key: 'action-logs',
      visible: isAdmin || isAgency,
      wrapInMenuItemBox: false,
      icon: '/static/images/administration/menu.svg',
      title: 'Nhật ký hoạt động',
      href: '/app/manage/action-logs'
    },
    {
      key: 'safe-zone',
      visible: isUser,
      wrapInMenuItemBox: false,
      icon: '/static/images/administration/safe-zone.svg',
      title: 'Quản trị vùng giám sát',
      href: '/app/manage/safe-zone'
    },
    {
      key: 'geocode-sources',
      visible: isAdmin,
      wrapInMenuItemBox: false,
      icon: '/static/images/administration/mapsetting.svg',
      title: 'Quản trị bản đồ',
      href: '/app/manage/geocode-sources'
    }
  ];

  const content = !rolesReady ? emptyContent : isCustomerCare ? customerCareContent : (
    <Box height="100%" display="flex" flexDirection="column">
      <Box style={{ paddingTop: '16px' }}>
        <List>
          {mainMenuItems
            .filter(item => item.visible)
            .map(({ key, wrapInMenuItemBox, ...navItemProps }) =>
              wrapInMenuItemBox ? (
                <Box key={key} className={classes.menuItem}>
                  <NavItem {...navItemProps} />
                </Box>
              ) : (
                <NavItem key={key} {...navItemProps} />
              )
            )}
        </List>
      </Box>
      <Box flexGrow={1} />
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

NavBar.defaultProps = {
  onMobileClose: () => { },
  openMobile: false
};

export default NavBar;
